import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { PageTransition } from "@/components/PageTransition";
import { AnimatedMeshGradient } from "@/components/AnimatedMeshGradient";
import { HubCard } from "@/components/HubCard";
import { useT } from "@/lib/i18n";
import { HUBS } from "@/constants/testIds";

const photo = (id) => `https://images.unsplash.com/${id}?w=1600&q=80&auto=format&fit=crop`;

// Static hub data; text comes from i18n by index.
const HUB_META = [
  {
    coords: "6.2476° N · 75.5658° W",
    timeZone: "America/Bogota",
    tzLabel: "GMT-5",
    gradient: "linear-gradient(145deg, #0B4F4E 0%, #124E4D 45%, #8A4438 100%)",
    image: photo("photo-1590598016835-83cf3357ebc5"),
    rotation: "rotate-[1.4deg]",
    testId: HUBS.medellin,
  },
  {
    coords: "40.4168° N · 3.7038° W",
    timeZone: "Europe/Madrid",
    tzLabel: "GMT+1",
    gradient: "linear-gradient(145deg, #0B4F4E 0%, #0D5F5E 50%, #2F8A80 100%)",
    image: photo("photo-1543785734-4b6e564642f8"),
    rotation: "-rotate-[1deg]",
    testId: HUBS.madrid,
  },
  {
    coords: "34.7071° N · 33.0226° E",
    timeZone: "Asia/Nicosia",
    tzLabel: "GMT+2",
    gradient: "linear-gradient(145deg, #082E2D 0%, #0D5F5E 60%, #1E6E66 100%)",
    image: photo("photo-1630353695798-b19b04e39ef4"),
    rotation: "rotate-[0.8deg]",
    testId: HUBS.limassol,
  },
];

export default function Hubs() {
  const t = useT();
  const HUBS_LIST = HUB_META.map((m, i) => ({ ...m, ...t.hubs.items[i] }));

  return (
    <PageTransition>
      {/* HERO */}
      <section data-testid={HUBS.root} className="relative paper-grain overflow-hidden" style={{ background: "var(--nexxis-bg)" }}>
        <AnimatedMeshGradient intensity={1} />
        <div className="hero-fade-bottom" aria-hidden="true" />
        <div className="relative z-10 mx-auto max-w-[1200px] px-5 sm:px-8 pt-36 sm:pt-44 pb-16">
          <Reveal><div className="kicker mb-6">{t.hubs.heroKicker}</div></Reveal>
          <div className="grid grid-cols-12 gap-8 items-end">
            <Reveal as="div" className="col-span-12 lg:col-span-8">
              <h1
                className="font-display"
                style={{ fontSize: "clamp(40px, 5.5vw, 76px)", lineHeight: 1.05, letterSpacing: "-0.02em", color: "var(--nexxis-ink)", fontWeight: 500 }}
              >
                {t.hubs.heroTitle}
              </h1>
            </Reveal>
            <Reveal as="div" delay={0.08} className="col-span-12 lg:col-span-4">
              <div className="h-px mb-5" style={{ background: "#FF6857", width: 48 }} />
              <p className="font-ui" style={{ color: "var(--nexxis-ink-muted)", lineHeight: 1.7, fontSize: 15 }}>
                {t.hubs.heroBody}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* STACKING HUB CARDS (sticky) */}
      <section className="relative" style={{ background: "var(--nexxis-bg-alt)" }}>
        <div className="mx-auto max-w-[1040px] px-5 sm:px-8">
          {HUBS_LIST.map((h) => (
            <figure
              key={h.testId}
              className="sticky top-0 m-0 flex h-screen items-center justify-center"
            >
              <HubCard
                testId={h.testId}
                city={h.city}
                country={h.country}
                role={h.role}
                coords={h.coords}
                timeZone={h.timeZone}
                tzLabel={h.tzLabel}
                gradient={h.gradient}
                image={h.image}
                rotation={h.rotation}
              />
            </figure>
          ))}
        </div>
      </section>

      {/* CLOSING */}
      <section className="relative" style={{ background: "var(--nexxis-bg-alt)" }}>
        <div className="mx-auto max-w-[1200px] px-5 sm:px-8 pb-24 pt-6">
          <Reveal>
            <div className="grid grid-cols-12 items-end gap-6">
              <div className="col-span-12 lg:col-span-8">
                <h3
                  className="font-display"
                  style={{ fontSize: "clamp(26px, 3vw, 40px)", lineHeight: 1.1, letterSpacing: "-0.012em", color: "var(--nexxis-ink)", fontWeight: 500 }}
                >
                  {t.hubs.footTitle}
                </h3>
                <p className="font-ui mt-4" style={{ color: "var(--nexxis-ink-muted)", lineHeight: 1.7, fontSize: 15, maxWidth: 520 }}>
                  {t.hubs.footBody}
                </p>
              </div>
              <div className="col-span-12 lg:col-span-4 flex lg:justify-end">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-3 rounded-[10px] px-5 py-3 font-ui text-[13px]"
                  style={{ background: "#0D5F5E", color: "#F7F6F2", letterSpacing: "0.04em", fontWeight: 600 }}
                >
                  <span>{t.hubs.footLink}</span>
                  <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </PageTransition>
  );
}
